import React, { useEffect, useRef, useState } from 'react';
import './copyAnswer.css';

type State = 'idle' | 'copied' | 'failed';

/**
 * Copy one answer's prose, for the long ones that get pasted into a doc or a ticket.
 *
 * Sits in the turn's footer beside the elapsed time rather than floating over the text on
 * hover: a hover affordance is invisible to anyone not using a mouse, and this panel is
 * narrow enough that an overlay would cover the first line of the answer it belongs to.
 *
 * Shown on finished turns only. Mid-stream the answer is half-written, and a button that
 * copies a sentence and a half is worse than no button.
 */
export function CopyAnswer({ text }: { text: string }) {
  const [state, setState] = useState<State>('idle');
  // Cleared on unmount: a turn can be replaced by a re-render while the 2s is still
  // running, and setting state afterwards warns and does nothing useful.
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!text) return null;

  const copy = async () => {
    clearTimeout(timer.current);
    try {
      // Rejects on an insecure origin and where the permission is refused, and is
      // undefined in a few embedded webviews. Either way the answer is still on screen
      // to select by hand, so this says so rather than throwing.
      await navigator.clipboard?.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
    timer.current = setTimeout(() => setState('idle'), 2000);
  };

  return (
    <button
      type="button"
      className={`mcp-copy${state !== 'idle' ? ` mcp-copy--${state}` : ''}`}
      onClick={() => {
        copy().catch(() => undefined);
      }}
      // Icon-only, so the label is the accessible name and the hover title both.
      // It changes with the state: a screen reader announcing "Copy the answer"
      // after a successful copy is worse than no feedback at all.
      aria-label={LABEL[state]}
      title={LABEL[state]}
    >
      {/* Wrapped rather than styled directly. The FontAwesome kit replaces the <i>
          with an <svg> of its own, so the span is the element this file can rely on
          still being there, and FA's own class sets a display on the element it is
          applied to which would beat a centring declared alongside it. Both are
          written up in LeadProfileCard and both were found the hard way. */}
      <span className="mcp-copy-icon" aria-hidden="true">
        <i className={ICON[state]} />
      </span>
    </button>
  );
}

const LABEL: Record<State, string> = {
  idle: 'Copy the answer',
  copied: 'Copied',
  failed: 'Could not copy, select it and press Command C',
};

const ICON: Record<State, string> = {
  idle: 'far fa-copy',
  copied: 'fas fa-check',
  // Not a cross: nothing was lost and nothing is broken, the answer is still on
  // screen to select by hand. A cross reads as a failed write.
  failed: 'fas fa-hand-pointer',
};
